// Error handling utilities for API responses

export interface ApiError {
  status?: number;
  message?: string;
  data?: any;
  isAuthError?: boolean;
  isNetworkError?: boolean;
}

/**
 * Normalize error from various sources (axios, fetch, etc.)
 */
export const normalizeError = (error: any): ApiError => {
  // Axios error
  if (error.response) {
    return {
      status: error.response.status,
      message: error.response.data?.message || error.response.data?.error || error.response.statusText,
      data: error.response.data,
      isAuthError: error.response.status === 401 || error.response.status === 403,
      isNetworkError: false,
    };
  }
  
  // Network/request error
  if (error.request) {
    return {
      status: 0,
      message: 'Network error - please check your connection',
      data: null,
      isAuthError: false,
      isNetworkError: true,
    };
  }
  
  // Other error
  return {
    status: 500,
    message: error.message || 'An unexpected error occurred',
    data: null,
    isAuthError: false,
    isNetworkError: false,
  };
};

/**
 * Get user-friendly error message
 */
export const getErrorMessage = (error: any): string => {
  const normalized = normalizeError(error);
  
  if (normalized.isNetworkError) {
    return 'Unable to connect to the server. Please check your internet connection.';
  }
  
  if (normalized.isAuthError) {
    return 'Authentication failed. Please check your credentials.';
  }
  
  switch (normalized.status) {
    case 400:
      return normalized.message || 'Invalid request. Please check your input.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'The requested resource was not found.';
    case 500:
      return 'Server error. Please try again later.';
    case 503:
      return 'Service temporarily unavailable. Please try again later.';
    default:
      return normalized.message || 'An error occurred. Please try again.';
  }
};

/**
 * Check if error should trigger logout/redirect
 */
export const shouldRedirectToLogin = (error: any): boolean => {
  const normalized = normalizeError(error);
  
  // Only redirect for specific auth-related issues
  if (!normalized.isAuthError) return false;
  
  // Check for token expiration or invalid token messages
  const message = normalized.message?.toLowerCase() || '';
  const tokenRelated = message.includes('token') || 
                     message.includes('expired') || 
                     message.includes('invalid') ||
                     message.includes('unauthorized');
  
  return tokenRelated;
};

/**
 * Handle API error in component
 */
export const handleApiError = (
  error: any, 
  setError: (message: string) => void,
  options: { showGenericMessage?: boolean; prefix?: string } = {}
) => {
  const { showGenericMessage = false, prefix = '' } = options;
  
  let errorMessage = getErrorMessage(error);
  
  if (prefix) {
    errorMessage = `${prefix}: ${errorMessage}`;
  }
  
  if (showGenericMessage) {
    errorMessage = 'An error occurred. Please try again.';
  }
  
  setError(errorMessage);
  
  // Log detailed error for debugging
  // console.error('API Error:', {
  //   normalizedError: normalizeError(error),
  //   originalError: error
  // });
  
  // Return whether this error should trigger a redirect
  return shouldRedirectToLogin(error);
};

/**
 * Hook for consistent error handling across components
 */
export const useErrorHandler = () => {
  return {
    handleError: handleApiError,
    normalizeError,
    getErrorMessage,
    shouldRedirectToLogin,
  };
};
